import type { ReactElement } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { IRange } from "app/pages/sales-intelligence/types/range";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type FindEngagementFilter from "../../../models/EngagementFilter/EngagementFilter";
import type { IEngagementFilterDto } from "../../../types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "../../../utils/i18n";
import { formatNumber, formatTime, formatFloatingNumber } from "../../../utils/numberFormatters";
import { RangeSelectWithClearField } from "../../RangeSelectWithClearField/RangeSelectWithClearField";
import RangeSlider from "../../RangeSlider/RangeSlider";
import { FilterContentContainer } from "../styles";

const EngagementFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindEngagementFilter>): ReactElement | null => {
    const handleClear = (key: keyof IEngagementFilterDto) => () => {
        const updated = { ...value };
        delete updated[key];
        onFilterUpdate(updated);
    };

    const handleChange = (key: keyof IEngagementFilterDto, range: Partial<IRange<number>>) => {
        onFilterUpdate({
            ...value,
            [key]: range,
        });
    };

    const handleSliderChange = (key: keyof IEngagementFilterDto, range: Partial<IRange<number>>) => {
        if (is.undefined(range.from) && is.undefined(range.to)) {
            handleClear(key)();
            return;
        }

        handleChange(key, range);
    };

    return (
        <FilterContentContainer>
            <RangeSelectWithClearField
                field="avgPagesPerVisit"
                buckets={filter.options["avgPagesPerVisit"]}
                formatter={formatNumber}
                value={value?.avgPagesPerVisit}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="avgVisitDuration"
                buckets={filter.options["avgVisitDuration"]}
                formatter={formatTime}
                value={value?.avgVisitDuration}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSelectWithClearField
                field="avgVisitsPerUser"
                buckets={filter.options["avgVisitsPerUser"]}
                // @ts-expect-error FIXME: Type 'string | number' is not assignable to type 'string'
                formatter={formatFloatingNumber}
                value={value?.avgVisitsPerUser}
                onHandleChange={handleChange}
                onHandleClear={handleClear}
            />
            <RangeSlider
                name="bounceRate"
                title={translate(leadsSearchFiltersI18nKeys.filter("bounceRate"))}
                options={filter.options["bounceRate"]}
                value={value?.bounceRate}
                onAfterChange={(range: Partial<IRange<number>>) => handleSliderChange("bounceRate", range)}
            />
        </FilterContentContainer>
    );
};

export default EngagementFilter;
